/**
 * PostHog 前端分析增强功能
 * 提供更完整的用户行为追踪和业务指标监控
 */
import { trackEvent, trackPageView, identifyUser } from './analytics';
import { logger } from './logger';

/**
 * 用户行为追踪增强
 */
export const trackUserBehavior = {
  /**
   * 用户注册完成
   */
  userRegistered: async (userId: string, method: string, userProps: Record<string, any> = {}) => {
    await trackEvent('user registered', {
      registration_method: method,
      ...userProps
    });
    
    // 同时识别用户
    await identifyUser(userId, {
      registration_method: method,
      registration_date: new Date().toISOString(),
      ...userProps
    });
  },

  /**
   * 用户登录成功
   */
  userLoggedIn: async (userId: string, method: string, userProps: Record<string, any> = {}) => {
    await trackEvent('user logged in', {
      login_method: method,
      ...userProps
    });
    
    // 更新用户属性
    await identifyUser(userId, {
      last_login: new Date().toISOString(),
      login_method: method,
      ...userProps
    });
  },

  /**
   * 用户登出
   */
  userLoggedOut: async (userId?: string) => {
    await trackEvent('user logged out', {
      user_id: userId
    });
  },

  /**
   * 功能首次使用
   */
  featureFirstUse: async (featureName: string, context: Record<string, any> = {}) => {
    await trackEvent('feature first use', {
      feature_name: featureName,
      ...context
    });
  },

  /**
   * 功能使用
   */
  featureUsed: async (featureName: string, context: Record<string, any> = {}) => {
    await trackEvent('feature used', {
      feature_name: featureName,
      ...context
    });
  },

  /**
   * 搜索行为
   */
  searchPerformed: async (query: string, resultCount: number, context: Record<string, any> = {}) => {
    await trackEvent('search performed', {
      search_query: query.substring(0, 100), // 限制长度
      result_count: resultCount,
      has_results: resultCount > 0,
      ...context
    });
  },

  /**
   * 设置变更
   */
  settingChanged: async (settingName: string, oldValue: any, newValue: any, context: Record<string, any> = {}) => {
    await trackEvent('setting changed', {
      setting_name: settingName,
      old_value: String(oldValue).substring(0, 100),
      new_value: String(newValue).substring(0, 100),
      ...context
    });
  },

  /**
   * 错误发生
   */
  errorOccurred: async (errorType: string, errorMessage: string, context: Record<string, any> = {}) => {
    await trackEvent('error occurred', {
      error_type: errorType,
      error_message: errorMessage.substring(0, 200),
      ...context
    });
  }
};

/**
 * 项目相关事件追踪
 */
export const trackProjectEvents = {
  /**
   * 项目创建
   */
  projectCreated: async (projectData: Record<string, any>) => {
    await trackEvent('project created', {
      has_context: !!projectData.context,
      is_public: !!projectData.isPublic,
      title_length: projectData.title?.length || 0,
      context_length: projectData.context?.length || 0,
      source: 'frontend'
    });
  },

  /**
   * 项目编辑
   */
  projectEdited: async (projectId: string, changes: Record<string, any>) => {
    await trackEvent('project edited', {
      project_id: projectId,
      changed_fields: Object.keys(changes),
      field_count: Object.keys(changes).length,
      source: 'frontend'
    });
  },

  /**
   * 项目删除
   */
  projectDeleted: async (projectId: string, projectData: Record<string, any> = {}) => {
    await trackEvent('project deleted', {
      project_id: projectId,
      was_public: !!projectData.isPublic,
      had_tasks: projectData.taskCount > 0,
      source: 'frontend'
    });
  },

  /**
   * 项目分享
   */
  projectShared: async (projectId: string, shareMethod: string) => {
    await trackEvent('project shared', {
      project_id: projectId,
      share_method: shareMethod,
      source: 'frontend'
    });
  },

  /**
   * 项目访问（查看分享的项目）
   */
  projectViewed: async (projectId: string, isOwner: boolean, context: Record<string, any> = {}) => {
    await trackEvent('project viewed', {
      project_id: projectId,
      is_owner: isOwner,
      ...context
    });
  },

  /**
   * 项目合并
   */
  projectMerged: async (sourceProjectId: string, targetProjectId: string, context: Record<string, any> = {}) => {
    await trackEvent('project merged', {
      source_project_id: sourceProjectId,
      target_project_id: targetProjectId,
      source: 'frontend',
      ...context
    });
  }
};

/**
 * 任务相关事件追踪
 */
export const trackTaskEvents = {
  /**
   * 任务创建
   */
  taskCreated: async (taskData: Record<string, any>) => {
    await trackEvent('task created', {
      has_due_date: !!taskData.dueAt,
      has_project: !!taskData.projectId,
      has_description: !!taskData.notes,
      is_vital: !!taskData.vital,
      is_urgent: !!taskData.urgent,
      has_recurrence: !!taskData.recurrenceRule,
      title_length: taskData.title?.length || 0,
      source: 'frontend'
    });
  },

  /**
   * 任务完成/取消完成
   */
  taskToggled: async (taskId: string, completed: boolean, taskData: Record<string, any> = {}) => {
    await trackEvent(completed ? 'task completed' : 'task uncompleted', {
      task_id: taskId,
      task_title: taskData.title?.substring(0, 50) || '',
      has_due_date: !!taskData.dueAt,
      has_project: !!taskData.projectId,
      is_vital: !!taskData.vital,
      is_urgent: !!taskData.urgent,
      source: 'frontend'
    });
  },

  /**
   * 任务编辑
   */
  taskEdited: async (taskId: string, changes: Record<string, any>) => {
    await trackEvent('task edited', {
      task_id: taskId,
      changed_fields: Object.keys(changes),
      field_count: Object.keys(changes).length,
      source: 'frontend'
    });
  },

  /**
   * 任务删除
   */
  taskDeleted: async (taskId: string, taskData: Record<string, any> = {}) => {
    await trackEvent('task deleted', {
      task_id: taskId,
      was_completed: !!taskData.done,
      had_project: !!taskData.projectId,
      source: 'frontend'
    });
  },

  /**
   * 批量操作
   */
  tasksBatchOperation: async (operation: string, taskIds: string[], context: Record<string, any> = {}) => {
    await trackEvent('tasks batch operation', {
      operation,
      task_count: taskIds.length,
      ...context
    });
  }
};

/**
 * AI对话相关事件追踪
 */
export const trackChatEvents = {
  /**
   * 对话创建
   */
  conversationCreated: async (conversationData: Record<string, any>) => {
    await trackEvent('conversation created', {
      has_character: !!conversationData.characterId,
      source: 'frontend'
    });
  },

  /**
   * 消息发送
   */
  messageSent: async (messageData: Record<string, any>) => {
    await trackEvent('message sent', {
      message_type: messageData.role,
      has_attachments: !!messageData.attachments?.length,
      character_id: messageData.characterId,
      conversation_id: messageData.conversationId,
      message_length: messageData.content?.length || 0,
      source: 'frontend'
    });
  },

  /**
   * 语音识别使用
   */
  voiceRecognitionUsed: async (duration: number, success: boolean, context: Record<string, any> = {}) => {
    await trackEvent('voice recognition used', {
      duration_seconds: Math.round(duration / 1000),
      success,
      ...context
    });
  },

  /**
   * 文件上传
   */
  fileUploaded: async (fileType: string, fileSize: number, context: Record<string, any> = {}) => {
    await trackEvent('file uploaded', {
      file_type: fileType,
      file_size_kb: Math.round(fileSize / 1024),
      ...context
    });
  },

  /**
   * 对话选择
   */
  conversationSelected: async (conversationId: string, context: Record<string, any> = {}) => {
    await trackEvent('conversation selected', {
      conversation_id: conversationId,
      source: 'frontend',
      ...context
    });
  }
};

/**
 * 拖拽操作相关事件追踪
 */
export const trackDragEvents = {
  /**
   * 拖拽开始
   */
  dragStarted: async (dragType: string, itemId: string, context: Record<string, any> = {}) => {
    await trackEvent('drag started', {
      drag_type: dragType,
      item_id: itemId,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 拖拽结束
   */
  dragEnded: async (dragType: string, itemId: string, success: boolean, context: Record<string, any> = {}) => {
    await trackEvent('drag ended', {
      drag_type: dragType,
      item_id: itemId,
      success,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 拖拽取消
   */
  dragCancelled: async (dragType: string, itemId: string, reason: string, context: Record<string, any> = {}) => {
    await trackEvent('drag cancelled', {
      drag_type: dragType,
      item_id: itemId,
      cancel_reason: reason,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 拖拽到区域
   */
  draggedToArea: async (dragType: string, itemId: string, areaType: string, areaId: string, context: Record<string, any> = {}) => {
    await trackEvent('dragged to area', {
      drag_type: dragType,
      item_id: itemId,
      area_type: areaType,
      area_id: areaId,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 表单和编辑器相关事件追踪
 */
export const trackFormEvents = {
  /**
   * 表单打开
   */
  formOpened: async (formType: string, mode: string, context: Record<string, any> = {}) => {
    await trackEvent('form opened', {
      form_type: formType,
      form_mode: mode, // 'create' | 'edit' | 'view'
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 表单字段编辑
   */
  formFieldEdited: async (formType: string, fieldName: string, context: Record<string, any> = {}) => {
    await trackEvent('form field edited', {
      form_type: formType,
      field_name: fieldName,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 表单保存
   */
  formSaved: async (formType: string, success: boolean, context: Record<string, any> = {}) => {
    await trackEvent('form saved', {
      form_type: formType,
      success,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 表单取消
   */
  formCancelled: async (formType: string, hasChanges: boolean, context: Record<string, any> = {}) => {
    await trackEvent('form cancelled', {
      form_type: formType,
      has_changes: hasChanges,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 编辑器打开
   */
  editorOpened: async (editorType: string, context: Record<string, any> = {}) => {
    await trackEvent('editor opened', {
      editor_type: editorType,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 编辑器保存
   */
  editorSaved: async (editorType: string, changes: Record<string, any>, context: Record<string, any> = {}) => {
    await trackEvent('editor saved', {
      editor_type: editorType,
      changed_fields: Object.keys(changes),
      field_count: Object.keys(changes).length,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 快速操作相关事件追踪
 */
export const trackQuickActionEvents = {
  /**
   * 快速操作按钮点击
   */
  quickActionClicked: async (actionType: string, context: Record<string, any> = {}) => {
    await trackEvent('quick action clicked', {
      action_type: actionType,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 快速添加使用
   */
  quickAddUsed: async (itemType: string, method: string, context: Record<string, any> = {}) => {
    await trackEvent('quick add used', {
      item_type: itemType,
      add_method: method, // 'button' | 'drag' | 'keyboard'
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 批量操作使用
   */
  batchActionUsed: async (actionType: string, itemCount: number, context: Record<string, any> = {}) => {
    await trackEvent('batch action used', {
      action_type: actionType,
      item_count: itemCount,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 模态框和弹窗相关事件追踪
 */
export const trackModalEvents = {
  /**
   * 模态框打开
   */
  modalOpened: async (modalType: string, trigger: string, context: Record<string, any> = {}) => {
    await trackEvent('modal opened', {
      modal_type: modalType,
      trigger, // 'button' | 'menu' | 'shortcut' | 'auto'
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 模态框关闭
   */
  modalClosed: async (modalType: string, method: string, duration: number, context: Record<string, any> = {}) => {
    await trackEvent('modal closed', {
      modal_type: modalType,
      close_method: method, // 'button' | 'backdrop' | 'escape' | 'save'
      duration_seconds: Math.round(duration / 1000),
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 模态框操作
   */
  modalActionPerformed: async (modalType: string, actionType: string, context: Record<string, any> = {}) => {
    await trackEvent('modal action performed', {
      modal_type: modalType,
      action_type: actionType,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 手势和交互相关事件追踪
 */
export const trackGestureEvents = {
  /**
   * 手势操作
   */
  gesturePerformed: async (gestureType: string, context: Record<string, any> = {}) => {
    await trackEvent('gesture performed', {
      gesture_type: gestureType, // 'pinch' | 'swipe' | 'long_press' | 'double_tap'
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 快捷键使用
   */
  shortcutUsed: async (shortcutKey: string, action: string, context: Record<string, any> = {}) => {
    await trackEvent('shortcut used', {
      shortcut_key: shortcutKey,
      action,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 长按操作
   */
  longPressPerformed: async (elementType: string, action: string, context: Record<string, any> = {}) => {
    await trackEvent('long press performed', {
      element_type: elementType,
      action,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 专注模式相关事件追踪
 */
export const trackFocusEvents = {
  /**
   * 专注会话开始
   */
  focusSessionStarted: async (taskId: string, duration: number, context: Record<string, any> = {}) => {
    await trackEvent('focus session started', {
      task_id: taskId,
      planned_duration_minutes: Math.round(duration / 60000),
      ...context
    });
  },

  /**
   * 专注会话完成
   */
  focusSessionCompleted: async (taskId: string, actualDuration: number, plannedDuration: number, context: Record<string, any> = {}) => {
    await trackEvent('focus session completed', {
      task_id: taskId,
      actual_duration_minutes: Math.round(actualDuration / 60000),
      planned_duration_minutes: Math.round(plannedDuration / 60000),
      completion_rate: actualDuration / plannedDuration,
      ...context
    });
  },

  /**
   * 专注会话中断
   */
  focusSessionInterrupted: async (taskId: string, duration: number, reason: string, context: Record<string, any> = {}) => {
    await trackEvent('focus session interrupted', {
      task_id: taskId,
      duration_minutes: Math.round(duration / 60000),
      interruption_reason: reason,
      ...context
    });
  }
};

/**
 * 性能监控
 */
export const trackPerformance = {
  /**
   * 页面加载性能
   */
  pageLoadPerformance: async (pageName: string, loadTime: number, context: Record<string, any> = {}) => {
    await trackEvent('page load performance', {
      page_name: pageName,
      load_time_ms: loadTime,
      is_slow: loadTime > 3000,
      ...context
    });
  },

  /**
   * API请求性能
   */
  apiRequestPerformance: async (endpoint: string, method: string, duration: number, success: boolean, context: Record<string, any> = {}) => {
    await trackEvent('api request performance', {
      endpoint,
      method,
      duration_ms: duration,
      success,
      is_slow: duration > 5000,
      ...context
    });
  },

  /**
   * 组件渲染性能
   */
  componentRenderPerformance: async (componentName: string, renderTime: number, context: Record<string, any> = {}) => {
    await trackEvent('component render performance', {
      component_name: componentName,
      render_time_ms: renderTime,
      is_slow: renderTime > 100,
      ...context
    });
  }
};

/**
 * 用户体验事件追踪
 */
export const trackUXEvents = {
  /**
   * 用户满意度反馈
   */
  userFeedback: async (rating: number, feedback: string, context: Record<string, any> = {}) => {
    await trackEvent('user feedback', {
      rating,
      has_text_feedback: feedback.length > 0,
      feedback_length: feedback.length,
      ...context
    });
  },

  /**
   * 功能发现
   */
  featureDiscovered: async (featureName: string, discoveryMethod: string, context: Record<string, any> = {}) => {
    await trackEvent('feature discovered', {
      feature_name: featureName,
      discovery_method: discoveryMethod,
      ...context
    });
  },

  /**
   * 帮助文档访问
   */
  helpAccessed: async (helpTopic: string, context: Record<string, any> = {}) => {
    await trackEvent('help accessed', {
      help_topic: helpTopic,
      ...context
    });
  }
};

/**
 * 导航和路由相关事件追踪
 */
export const trackNavigationEvents = {
  /**
   * 页面访问（移动端补全）
   */
  pageViewed: async (pageName: string, context: Record<string, any> = {}) => {
    await trackEvent('page viewed', {
      page_name: pageName,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 标签切换
   */
  tabSwitched: async (fromTab: string, toTab: string, context: Record<string, any> = {}) => {
    await trackEvent('tab switched', {
      from_tab: fromTab,
      to_tab: toTab,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 路由变化
   */
  routeChanged: async (fromRoute: string, toRoute: string, context: Record<string, any> = {}) => {
    await trackEvent('route changed', {
      from_route: fromRoute,
      to_route: toRoute,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 返回按钮使用
   */
  backButtonUsed: async (fromPage: string, context: Record<string, any> = {}) => {
    await trackEvent('back button used', {
      from_page: fromPage,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 深度链接访问
   */
  deepLinkAccessed: async (linkType: string, linkTarget: string, context: Record<string, any> = {}) => {
    await trackEvent('deep link accessed', {
      link_type: linkType,
      link_target: linkTarget,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 搜索和过滤相关事件追踪
 */
export const trackSearchEvents = {
  /**
   * 搜索查询执行
   */
  searchPerformed: async (query: string, scope: string, resultCount: number, context: Record<string, any> = {}) => {
    await trackEvent('search performed', {
      search_query: query.substring(0, 100),
      search_scope: scope,
      result_count: resultCount,
      has_results: resultCount > 0,
      query_length: query.length,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 搜索结果交互
   */
  searchResultClicked: async (query: string, resultIndex: number, resultType: string, context: Record<string, any> = {}) => {
    await trackEvent('search result clicked', {
      search_query: query.substring(0, 100),
      result_index: resultIndex,
      result_type: resultType,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 过滤器使用
   */
  filterApplied: async (filterType: string, filterValue: string, context: Record<string, any> = {}) => {
    await trackEvent('filter applied', {
      filter_type: filterType,
      filter_value: filterValue,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 排序方式变更
   */
  sortChanged: async (sortType: string, sortDirection: string, context: Record<string, any> = {}) => {
    await trackEvent('sort changed', {
      sort_type: sortType,
      sort_direction: sortDirection,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 搜索清除
   */
  searchCleared: async (query: string, context: Record<string, any> = {}) => {
    await trackEvent('search cleared', {
      previous_query: query.substring(0, 100),
      query_length: query.length,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 设置和偏好相关事件追踪
 */
export const trackSettingsEvents = {
  /**
   * 主题模式切换
   */
  themeChanged: async (fromTheme: string, toTheme: string, context: Record<string, any> = {}) => {
    await trackEvent('theme changed', {
      from_theme: fromTheme,
      to_theme: toTheme,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 语言设置变更
   */
  languageChanged: async (fromLanguage: string, toLanguage: string, context: Record<string, any> = {}) => {
    await trackEvent('language changed', {
      from_language: fromLanguage,
      to_language: toLanguage,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 通知偏好设置
   */
  notificationPreferenceChanged: async (preferenceType: string, enabled: boolean, context: Record<string, any> = {}) => {
    await trackEvent('notification preference changed', {
      preference_type: preferenceType,
      enabled,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 角色卡选择
   */
  characterSelected: async (characterId: string, characterName: string, context: Record<string, any> = {}) => {
    await trackEvent('character selected', {
      character_id: characterId,
      character_name: characterName,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 设置页面访问
   */
  settingsPageAccessed: async (settingsSection: string, context: Record<string, any> = {}) => {
    await trackEvent('settings page accessed', {
      settings_section: settingsSection,
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 日历和时间管理相关事件追踪
 */
export const trackCalendarEvents = {
  /**
   * 日历视图切换
   */
  calendarViewChanged: async (fromView: string, toView: string, context: Record<string, any> = {}) => {
    await trackEvent('calendar view changed', {
      from_view: fromView,
      to_view: toView,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 日期选择和导航
   */
  dateNavigated: async (navigationType: string, targetDate: string, context: Record<string, any> = {}) => {
    await trackEvent('date navigated', {
      navigation_type: navigationType,
      target_date: targetDate,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 日历事件点击
   */
  calendarEventClicked: async (eventType: string, eventId: string, context: Record<string, any> = {}) => {
    await trackEvent('calendar event clicked', {
      event_type: eventType,
      event_id: eventId,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 时间段选择
   */
  timeSlotSelected: async (startTime: string, endTime: string, context: Record<string, any> = {}) => {
    await trackEvent('time slot selected', {
      start_time: startTime,
      end_time: endTime,
      duration_minutes: Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000),
      ui_version: 'v3',
      ...context
    });
  }
};

/**
 * 通用工具函数
 */
export const analyticsUtils = {
  /**
   * 安全地追踪事件（捕获错误）
   */
  safeTrack: async (eventName: string, properties: Record<string, any> = {}) => {
    try {
      await trackEvent(eventName, properties);
    } catch (error) {
      logger.error(`Failed to track event: ${eventName}`, error as Error, properties);
    }
  },

  /**
   * 批量追踪事件
   */
  batchTrack: async (events: Array<{ name: string; properties: Record<string, any> }>) => {
    for (const event of events) {
      await analyticsUtils.safeTrack(event.name, event.properties);
    }
  },

  /**
   * 追踪用户会话开始
   */
  sessionStarted: async (context: Record<string, any> = {}) => {
    await trackEvent('session started', {
      timestamp: new Date().toISOString(),
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 追踪用户会话结束
   */
  sessionEnded: async (duration: number, context: Record<string, any> = {}) => {
    await trackEvent('session ended', {
      session_duration_minutes: Math.round(duration / 60000),
      timestamp: new Date().toISOString(),
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 追踪功能使用深度
   */
  featureEngagement: async (featureName: string, engagementLevel: string, context: Record<string, any> = {}) => {
    await trackEvent('feature engagement', {
      feature_name: featureName,
      engagement_level: engagementLevel,
      ui_version: 'v3',
      ...context
    });
  },

  /**
   * 追踪用户流程完成
   */
  flowCompleted: async (flowName: string, stepCount: number, duration: number, context: Record<string, any> = {}) => {
    await trackEvent('flow completed', {
      flow_name: flowName,
      step_count: stepCount,
      duration_seconds: Math.round(duration / 1000),
      ui_version: 'v3',
      ...context
    });
  }
};
